import { Fragment, useState, useEffect } from "react";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonFooter,
  useIonAlert,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { close, addOutline } from "ionicons/icons";
import { useSelectMemberPopover } from "../hooks/useSelectMemberPopover";
import {
  useGroupMembersBulk,
  MemberRole,
} from "../hooks/useGroupMembersBulk";
import ContactChips, { ContactChipItem } from "./ContactChips";
import { displayMsg, Spinner } from "pymble-ui/components";

import "./BulkMembers.css";

interface ContactItem {
  _id: string;
  Name?: string;
  ContactIdLookup?: string;
  [key: string]: unknown;
}

interface BulkMembersProps {
  onDismiss: () => void;
  selectedGroups: (string | number)[];
}

interface BulkResult {
  status: string;
  modified?: number;
}

const BulkMembers: React.FC<BulkMembersProps> = ({
  onDismiss,
  selectedGroups,
}) => {
  const [selectedContacts, setSelectedContacts] = useState<ContactItem[]>([]);
  const [allowAction, setAllowAction] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [newRole, setNewRole] = useState<string | null>(null);

  const [confirmAction] = useIonAlert();

  const { presentContactPopover, selectedMember, MemberModal } = useSelectMemberPopover(
    selectedContacts,
    "Find New Admins"
  );

  const { upsertMembers, deleteMembers } = useGroupMembersBulk();

  useEffect(() => {
    if (selectedMember) {
      setSelectedContacts([...selectedContacts, selectedMember as ContactItem]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedContacts causes infinite loop
  }, [selectedMember]);

  const handleClearSelectedContacts = () => {
    setSelectedContacts([]);
  };
  const handleRemoveContact = (contact: ContactChipItem) => {
    const newSelected = selectedContacts.filter(
      (c) => c.ContactIdLookup !== contact.ContactIdLookup
    );
    setSelectedContacts(newSelected);
  };

  const confirmBulkDeleteMembers = () => {
    confirmAction({
      cssClass: "bulk-action-alert",
      header: "Confirm",
      message: `Delete ${selectedContacts.length} member${
        selectedContacts.length > 1 ? "s" : ""
      } from ${selectedGroups.length} group${
        selectedGroups.length > 1 ? "s" : ""
      }?`,
      buttons: [
        "Cancel",
        {
          text: "Go",
          handler: () => handleDeleteMembers(),
        },
      ],
      onDidDismiss: () => console.log("Delete cancelled"),
    });
  };

  const handleDeleteMembers = async () => {
    setShowSpinner(true);
    const groups = selectedGroups as string[];
    const contacts = selectedContacts.map((c) => c.ContactIdLookup ?? c._id);

    const result = await deleteMembers(groups, contacts) as BulkResult;

    if (result.status !== "ok") {
      await displayMsg(
        "Oops, could not delete members.  Please check and try again.",
        {
          type: "danger",
          position: "middle",
          duration: 5000,
        }
      );
    } else {
      await displayMsg(
        `Delete successful. ${result.modified} records modified.`,
        {
          type: "success",
          position: "middle",
          duration: 3000,
        }
      );
    }
    setShowSpinner(false);
  };

  const confirmBulkUpdateMembers = () => {
    confirmAction({
      cssClass: "bulk-action-alert",
      header: "Confirm",
      message: `Change ${selectedContacts.length} contact${
        selectedContacts.length > 1 ? "s" : ""
      } to role ${newRole} in ${selectedGroups.length} group${
        selectedGroups.length > 1 ? "s" : ""
      }?`,
      buttons: [
        "Cancel",
        {
          text: "Go",
          handler: () => handleChangeMembers(),
        },
      ],
      onDidDismiss: () => console.log("Update cancelled"),
    });
  };

  const handleChangeMembers = async () => {
    setShowSpinner(true);
    const groups = selectedGroups as string[];
    const contacts = selectedContacts.map((c) => c.ContactIdLookup ?? c._id);

    const result = await upsertMembers(
      "change_role",
      newRole as MemberRole,
      groups,
      contacts
    ) as BulkResult;

    if (result.status !== "ok") {
      await displayMsg(
        "Oops, could not update members.  Please check and try again.",
        {
          type: "danger",
          position: "middle",
          duration: 5000,
        }
      );
    } else {
      await displayMsg(
        `Update successful. ${result.modified} records modified.`,
        {
          type: "success",
          position: "middle",
          duration: 3000,
        }
      );
    }
    setShowSpinner(false);
  };

  // Manage our allow bulk action state
  useEffect(() => {
    if (
      Array.isArray(selectedContacts) &&
      selectedContacts.length &&
      Array.isArray(selectedGroups) &&
      selectedGroups.length
    ) {
      setAllowAction(true);
    } else {
      setAllowAction(false);
    }
  }, [selectedGroups, selectedContacts]);

  return (
    <Fragment>
      <IonHeader>
        <IonToolbar color="light">
          <IonTitle>Bulk Group Member Update</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" size="small" onClick={onDismiss}>
              <IonIcon slot="icon-only" icon={close} size="small" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonToolbar>
          <IonTitle>Select Contacts for Operation</IonTitle>
          <IonButtons slot="end">
            <IonButton
              className="ion-text-capitalize"
              color="secondary"
              fill="solid"
              onClick={() =>
                presentContactPopover({
                  cssClass: "group-member-modal",
                })
              }
            >
              <IonIcon slot="icon-only" icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonGrid>
          <IonRow>
            <IonCol>
              <ContactChips
                contacts={selectedContacts}
                onClearContacts={handleClearSelectedContacts}
                onRemoveContact={handleRemoveContact}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonToolbar>
          <IonTitle>Select New Role</IonTitle>
          <IonButtons slot="end">
            <IonSelect
              value={newRole}
              placeholder="Select New Role"
              onIonChange={(e) => setNewRole(e.detail.value)}
            >
              <IonSelectOption value="Member">Member</IonSelectOption>
              <IonSelectOption value="Admin">Admin</IonSelectOption>
              <IonSelectOption value="Editor">Editor</IonSelectOption>
              <IonSelectOption value="Guest">Guest</IonSelectOption>
              <IonSelectOption value="None">None</IonSelectOption>
            </IonSelect>
          </IonButtons>
        </IonToolbar>
        {MemberModal}
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              className="ion-text-capitalize"
              color="secondary"
              fill="solid"
              onClick={confirmBulkDeleteMembers}
              disabled={!allowAction}
            >
              Delete Members
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            {showSpinner && <Spinner />}
            <IonButton
              className="ion-text-capitalize"
              color="secondary"
              fill="solid"
              onClick={confirmBulkUpdateMembers}
              disabled={!allowAction || !newRole}
            >
              Change Role {newRole && `to ${newRole}`}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </Fragment>
  );
};

export default BulkMembers;
